import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import { X, Send, AlertTriangle } from "lucide-react";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const scaleIn = keyframes`
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(23, 23, 23, 0.4);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  animation: ${fadeIn} 0.2s ease-out;
  padding: 16px;
`;

const ModalContainer = styled.div`
  background: #ffffff;
  border-radius: 20px;
  width: min(100%, 460px);
  box-shadow: 0 24px 64px rgba(23, 23, 23, 0.15);
  animation: ${scaleIn} 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  overflow: hidden;
  border: 1px solid rgba(23, 23, 23, 0.08);
`;

const ModalHeader = styled.div`
  padding: 18px 24px;
  background: #dc2626; /* Warning red color */
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #ffffff;
`;

const ModalTitle = styled.h3`
  font-family: "Urbanist", sans-serif;
  font-size: 18px;
  font-weight: 800;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  transition: background 0.15s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

const ModalBody = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AlertBanner = styled.div`
  display: flex;
  gap: 12px;
  background: #fef2f2;
  border: 1px solid #fee2e2;
  border-radius: 12px;
  padding: 14px;
  color: #991b1b;
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  line-height: 1.4;
  font-weight: 600;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FormLabel = styled.label`
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(23, 23, 23, 0.6);
`;

const TextInput = styled.input`
  height: 44px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  border-radius: 10px;
  padding: 0 14px;
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #171717;
  outline: none;
  background: #fbf9fb;
  transition: all 0.15s ease;
  &:focus {
    border-color: #dc2626;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15);
  }
`;

const SelectInput = styled.select`
  height: 44px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  border-radius: 10px;
  padding: 0 14px;
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #171717;
  outline: none;
  background: #fbf9fb;
  cursor: pointer;
  transition: all 0.15s ease;
  &:focus {
    border-color: #dc2626;
    background: #ffffff;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.15);
  }
`;

const MessagePreview = styled.div`
  background: #f3f4f6;
  border-radius: 10px;
  padding: 12px 14px;
  font-family: "Urbanist", sans-serif;
  font-size: 14px;
  color: #4b5563;
  line-height: 1.4;
  border: 1px dashed rgba(23, 23, 23, 0.1);
  max-height: 100px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 8px;
`;

const CancelButton = styled.button`
  flex: 1;
  height: 48px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  border-radius: 12px;
  background: #ffffff;
  color: #171717;
  font-family: "Urbanist", sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;
  &:hover {
    background: #f9fafb;
    border-color: rgba(23, 23, 23, 0.2);
  }
`;

const ConfirmButton = styled.button`
  flex: 2;
  height: 48px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(144.01deg, #dc2626 0%, #991b1b 130%);
  color: #ffffff;
  font-family: "Urbanist", sans-serif;
  font-size: 15px;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 8px 20px rgba(220, 38, 38, 0.22);
  transition: all 0.15s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(220, 38, 38, 0.28);
  }
  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
    transform: none;
  }
`;

interface TemplateFallbackModalProps {
  onClose: () => void;
  onConfirm: (customerName: string, category: "marketing" | "utility" | "authentication") => void;
  defaultCustomerName: string;
  messageText: string;
  submitting: boolean;
}

const TemplateFallbackModal: React.FC<TemplateFallbackModalProps> = ({
  onClose,
  onConfirm,
  defaultCustomerName,
  messageText,
  submitting,
}) => {
  const [customerName, setCustomerName] = useState(defaultCustomerName || "Customer");
  const [category, setCategory] = useState<"marketing" | "utility" | "authentication">("marketing");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(customerName, category);
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <AlertTriangle size={20} />
            24-Hour Window Closed
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={18} />
          </CloseButton>
        </ModalHeader>
        <form onSubmit={handleSubmit}>
          <ModalBody>
            <AlertBanner>
              The 24-hour free messaging window for this customer has closed. This message will be chargable.
            </AlertBanner>

            <FormGroup>
              <FormLabel>Message Preview</FormLabel>
              <MessagePreview>{messageText}</MessagePreview>
            </FormGroup>

            <FormGroup>
              <FormLabel>Customer Name</FormLabel>
              <TextInput
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="e.g. Shalini"
                required
              />
            </FormGroup>



            <ButtonGroup>
              <CancelButton type="button" onClick={onClose} disabled={submitting}>
                Cancel
              </CancelButton>
              <ConfirmButton type="submit" disabled={submitting}>
                <Send size={16} />
                {submitting ? "Sending Message..." : "Send Message"}
              </ConfirmButton>
            </ButtonGroup>
          </ModalBody>
        </form>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default TemplateFallbackModal;
